import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import useStyles from "./styles";
import CrmService, { addDaysYmd, apiError, studentFormPath, studentRemovalWarning, suggestedStatus, todayYmd } from "./crmService";
import { showAppConfirm } from "../../../context/AlertContext";
import { ChoiceChips, TimeField12, useCrmSettings, withExtras } from "./crmUi";

const QUICK_DATES = [
  { label: "Tomorrow", days: 1 },
  { label: "In 3 days", days: 3 },
  { label: "Next week", days: 7 },
];

/**
 * Log a follow-up in a few taps: how (chips) → outcome (chips) → next date
 * (quick picks). Status is preselected from the outcome and can be changed.
 */
export default function FollowUpDialog({ open, enquiry, onClose, onSaved }) {
  const history = useHistory();
  const classes = useStyles();
  const { settings, options, tz } = useCrmSettings();
  const [form, setForm] = useState(null);
  const [statusTouched, setStatusTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open || !enquiry) return;
    setForm({
      type: "Call",
      outcome: "",
      notes: "",
      statusAfter: enquiry.status,
      lostReason: "",
      nextDate: "",
      nextTime: "",
      staffName: enquiry.assignedTo || "",
      at: todayYmd(tz),
    });
    setStatusTouched(false);
    setErrors({});
    setMessage("");
  }, [open, enquiry, tz]);

  if (!open || !enquiry || !form) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setChoice = (key, value) => {
    const next = { ...form, [key]: value };
    if (!statusTouched) next.statusAfter = suggestedStatus(enquiry.status, next.outcome, next.type);
    if (key === "outcome" && value === "Visit scheduled" && !form.nextDate) next.nextDate = addDaysYmd(todayYmd(tz), 1);
    setForm(next);
  };
  const closed = (options?.closedStatuses || ["Admitted", "Lost"]).includes(form.statusAfter);
  const today = todayYmd(tz);

  const save = async () => {
    if (enquiry.status === "Admitted" && enquiry.studentId && form.statusAfter !== "Admitted") {
      const ok = await showAppConfirm(`Change ${enquiry.enquiryNo} from Admitted to ${form.statusAfter}?${studentRemovalWarning(enquiry)}`);
      if (!ok) return;
    }
    setSaving(true);
    setErrors({});
    setMessage("");
    try {
      const body = {
        type: form.type,
        outcome: form.outcome,
        notes: form.notes,
        statusAfter: form.statusAfter,
        lostReason: form.statusAfter === "Lost" ? form.lostReason : "",
        nextFollowUpDate: closed || !form.nextDate ? "" : form.nextTime ? `${form.nextDate}T${form.nextTime}` : form.nextDate,
        staffName: form.staffName,
        at: form.at && form.at !== today ? form.at : "",
      };
      const res = await CrmService.addFollowUp(enquiry._id, body);
      onSaved && onSaved(res);
      // Admitted: go straight to student registration, pre-filled from this enquiry.
      if (form.statusAfter === "Admitted" && !enquiry.studentId) {
        history.push(studentFormPath(enquiry._id));
      }
    } catch (err) {
      const e = apiError(err);
      setErrors(e.errors);
      setMessage(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" classes={{ paper: classes.dialogPaper }}>
      <DialogTitle disableTypography style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 600 }}>Log follow-up</div>
          <div className={classes.muted}>
            {enquiry.parentName || enquiry.childName} · {enquiry.enquiryNo}
          </div>
        </div>
        <IconButton aria-label="Close" onClick={onClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <div className={classes.section}>
          <span className={classes.fieldLabel}>How did you follow up? *</span>
          <ChoiceChips
            ariaLabel="Follow-up type"
            options={options?.followUpTypes || []}
            value={form.type}
            onChange={(v) => setChoice("type", v)}
          />
          {errors.type && <div className={classes.error}>{errors.type}</div>}
        </div>

        <div className={classes.section}>
          <span className={classes.fieldLabel}>Outcome</span>
          <ChoiceChips
            ariaLabel="Outcome"
            options={options?.followUpOutcomes || []}
            value={form.outcome}
            onChange={(v) => setChoice("outcome", v)}
          />
        </div>

        <div className={classes.section}>
          <TextField
            label="Notes"
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            placeholder="What did the parent say?"
            inputProps={{ maxLength: 4000 }}
          />
        </div>

        <div className={`${classes.grid2} ${classes.section}`}>
          <TextField
            select
            label="Status after this follow-up"
            value={form.statusAfter}
            onChange={(e) => {
              setStatusTouched(true);
              set({ statusAfter: e.target.value });
            }}
            variant="outlined"
            size="small"
            fullWidth
            error={Boolean(errors.statusAfter)}
          >
            {(options?.statuses || []).map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          {form.statusAfter === "Lost" ? (
            <TextField
              select
              label="Lost reason *"
              value={form.lostReason}
              onChange={(e) => set({ lostReason: e.target.value })}
              variant="outlined"
              size="small"
              fullWidth
              error={Boolean(errors.lostReason)}
              helperText={errors.lostReason}
            >
              {(settings?.lostReasons || []).map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            (settings?.staff || []).length > 0 && (
              <TextField
                select
                label="Done by"
                value={form.staffName}
                onChange={(e) => set({ staffName: e.target.value })}
                variant="outlined"
                size="small"
                fullWidth
              >
                <MenuItem value="">Me</MenuItem>
                {withExtras(settings.staff.map((s) => s.name), enquiry.assignedTo).map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            )
          )}
        </div>

        {!closed && (
          <div className={classes.section}>
            <span className={classes.fieldLabel}>Next follow-up</span>
            <div className={classes.chips} style={{ marginBottom: 10 }}>
              {QUICK_DATES.map((q) => {
                const value = addDaysYmd(today, q.days);
                const on = form.nextDate === value;
                return (
                  <button
                    key={q.label}
                    type="button"
                    className={`${classes.chip} ${on ? classes.chipOn : ""}`}
                    aria-pressed={on}
                    onClick={() => set({ nextDate: on ? "" : value })}
                  >
                    {q.label}
                  </button>
                );
              })}
            </div>
            <div className={classes.grid2}>
              <TextField
                type="date"
                label="Date"
                value={form.nextDate}
                onChange={(e) => set({ nextDate: e.target.value })}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.nextFollowUpDate)}
                helperText={errors.nextFollowUpDate || (!form.nextDate ? "No date: it will show under no follow-up later." : "")}
              />
              <TimeField12
                label="Time (optional)"
                value={form.nextTime}
                onChange={(nextTime) => set({ nextTime })}
                disabled={!form.nextDate}
              />
            </div>
          </div>
        )}

        <TextField
          type="date"
          label="Follow-up done on"
          value={form.at}
          onChange={(e) => set({ at: e.target.value })}
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: today }}
          error={Boolean(errors.at)}
          helperText={errors.at || "Change only when logging an earlier follow-up."}
        />
        {message && !Object.keys(errors).length && <div className={classes.error}>{message}</div>}
      </DialogContent>
      <DialogActions style={{ padding: "12px 24px" }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          disabled={saving || !form.type || (form.statusAfter === "Lost" && !form.lostReason)}
          startIcon={saving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : null}
        >
          Save follow-up
        </Button>
      </DialogActions>
    </Dialog>
  );
}
